import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { FeatureDismantlingModel } from '../../models/feature-dismantling-model';
import { GetClaimCompareTranslationRepository, GetClaimFeatureRepository, SetClaimCompareTranslationCacheRepository } from '../claim-charting-repository';
import type { FeatureDismantlingService } from '../feature-dismantling-service';
import type { ClaimFeatureData, ClaimFeatureItem, FeatureDismantlingLocalData, FeatureDismantlingState, FeatureItem, GetClaimCompareTranslationParams } from '../../types';
import { CHAR_CONCAT_SET, ID_PREFIX } from '../../constants';
import type { StreamStatus } from '../../types/enum';
import { ClaimGoogleTransLang } from '../../types/enum';
import { LangTypeMode } from '@/domains/translation/enums';
import { IndexDBService } from '../index-data-base-service';
import { I18nService } from '@analytics/shared/services/i18n.interface';
import { Message } from '@patsnap/tahiti-core/common/message';
import type { ApiGetClaimFeatureResponse } from '@/infrastructure/api-functions/core-search-api/get-claim-feature-api';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import { ExportExcelService } from '../export-excel-service';

@injectable()
export class FeatureDismantlingServiceImpl implements FeatureDismantlingService {
    private _onStatesChangeEmitter = new Emitter<FeatureDismantlingState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;

    @inject(GetClaimFeatureRepository)
    private _getClaimFeatureRepository!: GetClaimFeatureRepository;
    @inject(GetClaimCompareTranslationRepository)
    private _getClaimCompareTranslationRepository!: GetClaimCompareTranslationRepository;
    @inject(SetClaimCompareTranslationCacheRepository)
    private _setClaimCompareTranslationCacheRepository!: SetClaimCompareTranslationCacheRepository;
    @inject(IndexDBService)
    private _indexDBService!: IndexDBService<FeatureDismantlingLocalData>;
    @inject(ExportExcelService)
    private _exportExcelService!: ExportExcelService;

    private _stream: PsHttpSSE<ApiGetClaimFeatureResponse> | null = null;

    constructor(
        @inject(FeatureDismantlingModel)
        private _model: FeatureDismantlingModel,
        @inject(Message)
        private _message: Message,
        @inject(I18nService)
        private _i18n: I18nService,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    public getModelData() {
        return this._model.modelData;
    }

    public updateModelData(data: Partial<FeatureDismantlingState>) {
        // 无外部引用可以用private
        this._model.update(() => ({
            ...this._model.modelData,
            ...data,
        }));
    }

    public get claimLang() {
        return this._model.modelData.claimLang;
    }

    public get transLang() {
        return this._model.modelData.transLang;
    }

    public get patentId() {
        return this._model.modelData.patentId;
    }

    private _setFeatureTranslationCache(feature: FeatureItem) {
        // 只在存在翻译语言，且翻译语言不等于原语言，是点合并行才需要进行当前行翻译缓存
        if (this.transLang !== this.claimLang && this.claimLang !== ClaimGoogleTransLang.DEFAULT) {
            const params = {
                patentId: this.patentId,
                transLang: this.transLang,
                text: feature.value,
                translation: feature.trans!,
            };
            this._setClaimCompareTranslationCacheRepository.setClaimCompareTranslationCache(params);
        }

    }

    public async getClaimFeatureData(patentId: string) {
        let data = this.getModelData();
        this._stream = await this._getClaimFeatureRepository.getClaimFeatures(
            patentId,
            (streamStatus: StreamStatus, content?: ClaimFeatureData) => {
                if (content) data = { ... data, ...content };
                this.updateModelData({
                    ...data,
                    streamStatus,
                    patentId,
                });
            },
            (error: { error_code?: number }) => {
                const errorCode = (error as { error_code?: number }).error_code || 400;
                // 超出使用容量错误码
                this._message.closeAll();
                this._message.warning({
                    message: this._i18n.t(`commonStatusCode.${errorCode}`),
                    showClose: true,
                });
            },
        );

    }

    private _closeUpstream() {
        this._stream?.close();
    }

    private _getConcatenationChar(lang: ClaimGoogleTransLang | string) {
        return lang === ClaimGoogleTransLang.CN ? '，' : ',';
    }

    private _concatenateFeatures(sourceFeature: FeatureItem, targetFeature: FeatureItem) {
        const concatenate = (source: string, target: string, char: string) => {
            const lastChar = source.slice(-1);
            return CHAR_CONCAT_SET.includes(lastChar) ? `${source}${target}` : `${source}${char}${target}`;
        };

        sourceFeature.value = concatenate(sourceFeature.value, targetFeature.value, this._getConcatenationChar(this.claimLang));

        if (sourceFeature.trans && targetFeature.trans) {
            sourceFeature.trans = concatenate(sourceFeature.trans, targetFeature.trans, this._getConcatenationChar(this.transLang));
        }
    }

    private _findClaimItemByName(claims: ClaimFeatureItem[], name: string) {
        return claims.find(item => item.name === name);
    }

    public mergeClaimFeature(name: string, source: number, target: number) {
        const optData = this.getModelData();
        const claimItem = this._findClaimItemByName(optData.claims, name);

        if (!claimItem) return;

        const sourceFeature = claimItem.features[source];
        const targetFeature = claimItem.features[target];

        if (!sourceFeature || !targetFeature) return;

        this._concatenateFeatures(sourceFeature, targetFeature);
        claimItem.features.splice(target, 1);
        this.updateModelData(optData);
        this._setFeatureTranslationCache(sourceFeature);
    }

    public deleteClaimFeature(name: string, index: number) {
        // 根据name找到当前的claim item，再根据index删除feature数据
        // 注意，如果features被删除空，则权利要求也要删除
        const optData = this.getModelData();
        const claimItem = this._findClaimItemByName(optData.claims, name);

        if (!claimItem) return;

        claimItem.features.splice(index, 1);

        if (claimItem.features.length === 0) {
            optData.claims = optData.claims.filter(item => item.name !== name);
        }

        this.updateModelData(optData);
    }

    private _updateTransLoading(val: boolean) {
        this.updateModelData({ transLoading: val });
    }

    public async getFeatureTranslation(params: GetClaimCompareTranslationParams) {
        // filter获取文本
        // format获取翻译数据，format过程在repository里
        // 提供一个思路，参数和返回的结果是一个拍平的string list，可以根据去循环原始的claims，然后按features进行切割，组装成一个和claims同结构的trans
        // 切换翻译原文时，清除下面翻译内容；切换翻译当原文和翻译语言一样时，直接展示原文，不请求接口
        this._updateTransLoading(true);
        const claimTrans = (params.langType === LangTypeMode.ORIGINAL || params.transLang === this.claimLang) ? {} : await this._getClaimCompareTranslationRepository.getClaimCompareTranslation(params);
        const optData = this.getModelData();
        optData.claims.map(claim => {
            claim.features.map(feature => {
                feature.trans = params.transLang !== this.claimLang ? claimTrans[feature.value] : feature.value;
                return feature;
            });
            return claim;
        });
        optData.transLang = params.transLang;
        optData.transLangType = params.langType;
        optData.transLoading = false;
        this.updateModelData(optData);
    }

    public async saveClaimFeatureData(data: ClaimFeatureItem[]) {
        const allKeys = await this._indexDBService.getAllKeys();
        const prefixKeys = allKeys.filter(key => key.startsWith(ID_PREFIX));
        if (prefixKeys.length > 0) {
            await this._indexDBService.clearData(prefixKeys[0]);
        }
        const localData = {
            patentId: this.patentId,
            transLang: this.transLang,
            claimLang: this.claimLang,
            patentSN: this._model.modelData.patentSN,
            claims: data,
        };

        this._indexDBService.saveData(`${ID_PREFIX}${this.patentId}`, localData);
    }

    public async exportExcel(tableData: ClaimFeatureItem[]) {
        const excelParams = {
            hasTransLang: this.transLang && this.transLang !== ClaimGoogleTransLang.DEFAULT,
            claimPN: this._model.modelData.patentSN,
        };
        this._exportExcelService.exportClaimExcel(tableData, excelParams);
    }

    public reset() {
        this._closeUpstream();
        this.updateModelData(this._model.defaultModelData);
    }
}
